import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Col, Form, Input, Row, Select, Space, Typography, Modal, Button, message, Popconfirm, Divider, Tooltip, Popover, Tag } from 'antd';
import moment from 'moment';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import './styles.css';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { useCampaignSaveManager } from './hooks/useCampaignSaveManager';
import { HasAccess } from '@permify/react-role';
import { formatCampaignName } from '../../../core/utils/campaignNaming';
import { useTimezone } from '../../../core/hooks/useTimezone';
import { getCpmForCampaignType } from '../../../core/utils/pricing/cpmHelper';
import { orgTreeHasContract } from '../../../core/utils/pricing/orgPricing';
import { getEffectivePermission, isRealSessionProcessor } from '../../../core/permissions/effectivePermission';

const { Text } = Typography;

// Advertisers created under a Channel Partner have no contract with the platform
// — the platform bills the CP, and the CP bills them. Their Opportunity uses the
// Channel Partner record type and is serviced by the CP's Salesforce Account.
// CORE/ENTERPRISE advertisers contract directly and get the default record type.
// See getAllowedOrgLevels in core/utils/orgPermissions for the level hierarchy.
const CHANNEL_PARTNER_ADVERTISER_LEVELS = ['STANDARD', 'PROSPECTING'];
const SF_CHANNEL_PARTNER_RECORD_TYPE_ID = '0126g000000nzljAAA';

const CampaignRequestorDetails = ({ formValues, helpers }) => {
  const {
    childrenOrg,
    loadingChildrenOrgs,
    currentUserOrg,
    selectedAdvertiser,
    fetchChildrenOrgs,
    currentUser,
    currentCampaignOrder,
    updateCampaignOrder,
    updateCampaignOrderSalesforceId,
    createSalesforceOpportunity,
    loadingCreateSalesforceOpportunity,
    createSalesforceOpportunityLineItems,
    loadingCreateSalesforceOpportunityLineItems,
    createSalesforceContact,
    createSalesforceOpportunityContactRole,
    fetchSalesforceOpportunityProposalNumber,
    salesforceOpportunityProposalNumber,
    loadingSalesforceOpportunityProposalNumber,
  } = useCampaignRequestorContext();

  const [advertisers, setAdvertisers] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [ioSearchText, setIOSearchText] = useState('');
  const history = useHistory();
  const { saveCampaign } = useCampaignSaveManager();
  const { timezone } = useTimezone();

  // ─── Create Salesforce Opportunity modal state ──────────────────────────
  const [isSfOppModalVisible, setIsSfOppModalVisible] = useState(false);
  const [sfOppName, setSfOppName] = useState('');
  const [sfBudget, setSfBudget] = useState('');
  const [sfMonths, setSfMonths] = useState('');
  // const [sfDecisionMakerFirstName, setSfDecisionMakerFirstName] = useState('');
  // const [sfDecisionMakerLastName, setSfDecisionMakerLastName] = useState('');
  // const [sfFourthField, setSfFourthField] = useState('');
  const [isCreatingSfOpp, setIsCreatingSfOpp] = useState(false);

  // const advertiserHasContract =
  //   !!(selectedAdvertiser && selectedAdvertiser.pricingModel && selectedAdvertiser.pricingModel.hasContract === true);
  // const fourthFieldLabel = advertiserHasContract ? 'Phone Number' : 'Decision Maker Title';
  // const fourthFieldPlaceholder = advertiserHasContract
  //   ? 'Enter phone number'
  //   : 'Enter decision maker title';

  // Debounce ref for advertiser search
  const searchDebounceRef = useRef(null);

  // Bridge react-apollo@3 useLazyQuery (no awaitable) to a Promise. We register
  // a resolver here, fire the lazy query, and resolve when the hook's loading
  // flag transitions back to false.
  const proposalNumberResolverRef = useRef(null);

  useEffect(() => {
    if (
      proposalNumberResolverRef.current &&
      !loadingSalesforceOpportunityProposalNumber
    ) {
      const { resolve } = proposalNumberResolverRef.current;
      proposalNumberResolverRef.current = null;
      resolve(salesforceOpportunityProposalNumber);
    }
  }, [loadingSalesforceOpportunityProposalNumber, salesforceOpportunityProposalNumber]);

  useEffect(() => {
    if (childrenOrg && childrenOrg.length) {
      const options = childrenOrg.map((org) => ({
        value: org.id,
        label: org.name,
      }));
      // For cross-org campaigns, the selected advertiser may not be in
      // childrenOrg (e.g. parent org advertiser). Add it so the dropdown
      // displays the name instead of the raw ID.
      if (
        selectedAdvertiser &&
        !childrenOrg.some((org) => org.id === selectedAdvertiser.id)
      ) {
        options.unshift({
          value: selectedAdvertiser.id,
          label: selectedAdvertiser.name,
        });
      }
      setAdvertisers(options);
    } else {
      if (!selectedAdvertiser) return;

      setAdvertisers([
        {
          value: selectedAdvertiser.id,
          label: selectedAdvertiser.name,
        },
      ]);
    }
  }, [childrenOrg, selectedAdvertiser]);

  useEffect(() => {
    if (currentUser && currentUser.permission) {
      const authorized = currentUser.permission.isProcessor;
      setCanEdit(authorized);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUserOrg?.id) {
      handleFetchAdvertisers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserOrg?.id]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleFetchAdvertisers = useCallback((search = '') => {
    if (!currentUserOrg) return;
    fetchChildrenOrgs({
      variables: {
        where: {
          OR: [{ parentOrg_some: { id: currentUserOrg.id } }, { id: currentUserOrg.id }],
          name_contains: search,
        },
        orderBy: 'name_ASC',
        first: 1500, // TODO: Apply virtual scrolling and pagination
      },
    });
  }, [fetchChildrenOrgs, currentUserOrg]);

  // Debounced search handler for advertiser search
  const handleDebouncedAdvertiserSearch = useCallback((searchText) => {
    // Clear existing timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new timeout (500ms delay)
    searchDebounceRef.current = setTimeout(() => {
      handleFetchAdvertisers(searchText);
    }, 500);
  }, [handleFetchAdvertisers]);

  const handleNameChange = (e) => {
    // Strip disallowed characters in real-time, then propagate every change to
    // formValues so consumers (e.g. the Create-SF-Opportunity Tooltip/disabled
    // state) see the live value instead of a stale empty string.
    const cleanValue = e.target.value.replace(/[^a-zA-Z0-9\s\-_]/g, '');
    helpers.updateField('name', cleanValue);
  };

  const resetSfOppModal = () => {
    setIsSfOppModalVisible(false);
    setSfOppName('');
    setSfBudget('');
    setSfMonths('');
    // setSfDecisionMakerFirstName('');
    // setSfDecisionMakerLastName('');
    // setSfFourthField('');
  };

  const handleCreateSalesforceOpportunity = async () => {
    if (!selectedAdvertiser) {
      message.error('No advertiser selected');
      return;
    }

    // Refuse to guess. Without a level we cannot tell a Channel Partner deal from
    // a direct one, and the Opportunity would silently land on the wrong record
    // type — which this flow has no way to roll back.
    if (!selectedAdvertiser.level) {
      message.error(
        'Cannot determine the Opportunity record type: this advertiser has no org level. Re-select the advertiser and try again.',
      );
      return;
    }

    // One predicate decides everything below. Deriving the record type and the
    // billing account from different fields lets an Opportunity take its Amount
    // from one org and its record type from another.
    const isChannelPartnerOpp = CHANNEL_PARTNER_ADVERTISER_LEVELS.includes(
      selectedAdvertiser.level,
    );

    // Resolve the org that holds the platform contract — it is the Salesforce
    // Account that gets billed:
    //  - Channel Partner deals bill the first parentOrg with a contract.
    //  - Direct deals bill the advertiser itself.
    const contractOrg = isChannelPartnerOpp
      ? (selectedAdvertiser.parentOrg || []).find(
          (org) => org?.pricingModel?.hasContract === true,
        )
      : selectedAdvertiser;
    const resolvedAccountId = contractOrg?.salesforceId ?? null;
    const resolvedBillingEmail = contractOrg?.billingEmail ?? null;

    // The Opportunity is always booked against the advertiser's own Account.
    if (!selectedAdvertiser.salesforceId) {
      message.error('No Salesforce Account found: this advertiser has no Salesforce ID');
      return;
    }

    // On a Channel Partner deal the contract org is a different Account, so it
    // needs its own check. On a direct deal it is the advertiser, already checked.
    if (!resolvedAccountId) {
      message.error(
        'No Salesforce Account found: no parent org with a contract has a Salesforce ID',
      );
      return;
    }

    // Resolve both CPMs here, alongside the other early exits, because every
    // number this flow sends to Salesforce derives from them (impressions, the
    // Opportunity Amount, the line item UnitPrice and Cpm__c). A missing or
    // malformed rate yields NaN, and Salesforce would accept it — this flow has
    // no rollback. Exiting before the draft save and the Contact create keeps a
    // bad rate side-effect free.
    //   advertiserCPM     = the advertiser org's own cpmList[0].contractCPM.
    //                       Used to derive how many impressions the
    //                       advertiser's budget buys.
    //   channelPartnerCPM = the contract org's cpmList[0].contractCPM
    //                       (discounted cost basis the CP actually pays).
    //                       Multiplied by impressions to produce the SF opp
    //                       Amount.
    // parseFloat (not parseInt) so fractional CPMs like "5.50" are preserved —
    // SF's Cpm__c is a currency field and accepts decimals.
    const advertiserCPM = parseFloat(selectedAdvertiser.pricingModel?.cpmList?.[0]?.advertiserCPM);
    if (Number.isNaN(advertiserCPM) || advertiserCPM <= 0) {
      message.error(
        'Cannot create Opportunity: advertiser has no valid CPM rate. Verify the pricing model has a CPM list entry.',
      );
      return;
    }

    const contractCpmEntry = contractOrg?.pricingModel?.cpmList?.[0];
    const channelPartnerCPM =
      contractCpmEntry?.contractCPM != null
        ? parseFloat(String(contractCpmEntry.contractCPM).replace(/,/g, ''))
        : 7;
    // The `!= null` check above still lets a non-numeric string (e.g. '') through
    // as NaN, so the parsed value needs its own check.
    if (Number.isNaN(channelPartnerCPM) || channelPartnerCPM <= 0) {
      message.error(
        'Cannot create Opportunity: the contract org has no valid CPM rate. Verify the pricing model has a CPM list entry.',
      );
      return;
    }

    setIsCreatingSfOpp(true);

    try {
      // 1. Resolve modal values, falling back to existing form values
      const opportunityName = formValues.name;
      const opportunityAmount = formValues.budget;

      const formattedOpportunityName = formatCampaignName(
        {
          name: opportunityName,
          productCode: formValues.productCode,
          startDate: formValues.startDate,
          advertiser: selectedAdvertiser,
          type: formValues.type,
        },
        timezone,
      );

      // 2. Sync the modal values back to the form so the persisted draft
      //    reflects the user's edits.
      // helpers.updateField('name', opportunityName);
      helpers.updateField('budget', opportunityAmount);

      // 3. Save the campaign as a DRAFT first so we have a campaignOrder
      //    to attach the Salesforce Opportunity to. saveCampaign returns
      //    the campaignOrderId for both create and update flows.
      const allFormValues = helpers.form.getFieldsValue();
      const campaignData = {
        ...allFormValues,
        name: opportunityName,
        budget: opportunityAmount,
        advertiser: allFormValues.advertiser || selectedAdvertiser,
        advertiserId: allFormValues.advertiserId || selectedAdvertiser?.id,
        status: currentCampaignOrder?.status || 'DRAFT',
      };
      const isUpdate = !!currentCampaignOrder;
      const saveResult = await saveCampaign(
        campaignData,
        isUpdate,
        currentCampaignOrder,
        selectedAdvertiser,
      );
      if (!saveResult || !saveResult.success) {
        throw saveResult?.error || new Error('Failed to save campaign as draft');
      }
      const savedCampaignOrderId = saveResult.campaignOrderId;
      if (!savedCampaignOrderId) {
        throw new Error('Saved campaign order has no id');
      }

      // 4. (URL navigation is deferred until the SF flow finishes — see end of try
      //    block. Replacing the URL here triggers a `fetchCampaignOrder` race that
      //    can overwrite the cache with a stale snapshot before our IONumber write
      //    lands.)

      // 5. Build SF payloads from the resolved values.
      // Create the Contact first so we can attach it to the Opportunity via
      // Deal_Signatory__c (Lookup(Contact)) on create.
      const { data: contactData } = await createSalesforceContact({
        variables: { data: { lastName: resolvedBillingEmail ?? '' } },
      });
      const contactId = contactData?.createSalesforceContact;
      if (!contactId) {
        throw new Error('Salesforce did not return a Contact Id');
      }

      // The SF line item is hardcoded to MB-DIS-DISP-ATRG ("Standard Display
      // Awareness"). advertiserCPM / channelPartnerCPM are resolved and
      // validated above, before any side effect.
      const sfOpportunityCampaignType = 'DISPLAY';

      const initialImpressions = (1000 * opportunityAmount) / advertiserCPM
      // cost = CPM × impressions / 1000
      const salesforceCPBudget = channelPartnerCPM * initialImpressions / 1000

      const trimmedSfMonths = sfMonths.trim();
      const parsedSfMonths = trimmedSfMonths !== '' ? Number(trimmedSfMonths) : null;
      const lineItemQuantity =
        parsedSfMonths !== null && !Number.isNaN(parsedSfMonths) && parsedSfMonths > 0
          ? parsedSfMonths
          : 3;

      const opportunityPayload = {
        Name: formattedOpportunityName,
        StageName: 'Decision',
        CloseDate: new Date().toISOString().split('T')[0],
        // Always the advertiser's own Account; on a Channel Partner deal the CP
        // is carried alongside it in Serviced_Account__c.
        AccountId: selectedAdvertiser.salesforceId,
        Amount: isChannelPartnerOpp ? salesforceCPBudget : opportunityAmount,
        Contract_Type__c: "Auto-Renew",
        Number_of_months__c: lineItemQuantity,
        ...(isChannelPartnerOpp && {
          LeadSource: 'Partner',
          Serviced_Account__c: resolvedAccountId,
          RecordTypeId: SF_CHANNEL_PARTNER_RECORD_TYPE_ID,
          Deal_Signatory__c: contactId,
        }),
      };

      const lineItemsPayload = {
        PricebookEntryId: 'MB-DIS-DISP-ATRG',
        Quantity: lineItemQuantity,
        UnitPrice: salesforceCPBudget / lineItemQuantity,
        Description: 'Standard Display Awareness',
        Cpm__c: isChannelPartnerOpp ? channelPartnerCPM : advertiserCPM,
        ServiceDate: formValues.startDate.format("YYYY-MM-DD"),                                                                                                                                    
        Service_End_Datee__c: formValues.endDate.format("YYYY-MM-DD"),
      };

      const { data: oppData } = await createSalesforceOpportunity({
        variables: { opportunity: opportunityPayload },
      });

      const opportunityId = oppData?.createSalesforceOpportunity;
      if (!opportunityId) {
        throw new Error('Salesforce did not return an Opportunity Id');
      }

      // 6. Persist the SF opportunityId onto the campaignOrder.
      await updateCampaignOrderSalesforceId({
        variables: {
          where: { id: savedCampaignOrderId },
          data: { salesforceId: opportunityId },
        },
      });

      const sfOppLineItems = {
        opportunityId,
        lineItems: [lineItemsPayload],
      };

      await createSalesforceOpportunityLineItems({ variables: sfOppLineItems });

      await createSalesforceOpportunityContactRole({
        variables: {
          opportunityId,
          contactId,
          roleName: 'Decision Maker',
          isPrimary: true,
        },
      });

      // react-apollo@3 useLazyQuery doesn't return a promise — fire the query
      // and await the bridge useEffect that resolves with the hook state.
      const proposalNumber = await new Promise((resolve, reject) => {
        proposalNumberResolverRef.current = { resolve, reject };
        try {
          fetchSalesforceOpportunityProposalNumber({
            variables: { salesforceOpportunityId: opportunityId },
          });
        } catch (err) {
          proposalNumberResolverRef.current = null;
          reject(err);
        }
      });

      if (!proposalNumber) {
        throw new Error('Salesforce did not return a Proposal Number');
      }

      const { data: updateData } = await updateCampaignOrder({
        variables: {
          where: { id: savedCampaignOrderId },
          data: {
            additionalIONumbers: { create: { value: proposalNumber } },
          },
        },
      });

      const updatedCampaignOrder = updateData?.updateCampaignOrderRequestor;
      if (updatedCampaignOrder?.additionalIONumbers) {
        helpers.updateField('additionalIONumbers', updatedCampaignOrder.additionalIONumbers);
      }
      // The freshly created proposal number becomes the active IO — the user
      // should never have to manually re-select what we just created.
      helpers.updateField('IONumber', proposalNumber);

      // 8. Now that the SF round-trips and the IONumber write are done, update
      //    the URL so a refresh continues editing the same campaign. Doing this
      //    last avoids the race with `fetchCampaignOrder` overwriting the cache.
      if (!isUpdate) {
        history.replace(`/campaigns/requester/edit/${savedCampaignOrderId}`);
      }

      message.success(
        `Salesforce Opportunity created. Proposal Number ${proposalNumber} is now the active IO Number.`,
      );
      resetSfOppModal();
    } catch (error) {
      console.error('Error creating Salesforce Opportunity:', error);
      message.error(`Failed to create Salesforce Opportunity: ${error.message ?? error}`);
    } finally {
      setIsCreatingSfOpp(false);
    }
  };

  // Deleting an IO cascades into the CRM integration (Salesforce opportunity
  // link) and any PandaDoc attachments it brought in.
  const collectCascadeDeletions = (ioNumberValue) => {
    const integrationIdsToDelete = [];
    const attachmentIdsToDelete = [];

    (currentCampaignOrder?.externalCRMIntegrations || []).forEach((integration) => {
      const metadata = integration.metadata;
      const proposalNumber = metadata?.Proposal_Number__c;

      if (proposalNumber !== ioNumberValue) return;

      integrationIdsToDelete.push({ id: integration.id });

      const pandaDocRecords = metadata?.pandadoc__Panda_Doc_Documents__r?.records;
      if (!pandaDocRecords) return;

      pandaDocRecords.forEach((record) => {
        const uuid = record.pandadoc__UUID__c;
        if (!uuid) return;

        currentCampaignOrder.attachments?.forEach((attachment) => {
          if (attachment.referenceId === uuid) {
            attachmentIdsToDelete.push({ id: attachment.id });
          }
        });
      });
    });

    return { integrationIdsToDelete, attachmentIdsToDelete };
  };

  // Deleting the active IO promotes the most recent remaining one instead of
  // silently emptying a required field.
  const promoteMostRecentIO = (ioNumbers) => {
    const next = [...(ioNumbers || [])].sort((a, b) =>
      String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
    )[0];
    helpers.updateField('IONumber', next ? next.value : null);
  };

  const handleDeleteIONumber = async (ioNumberId, ioNumberValue) => {
    const { integrationIdsToDelete, attachmentIdsToDelete } = collectCascadeDeletions(ioNumberValue);

    if (!ioNumberId) {
      const currentAdditional = formValues.additionalIONumbers || [];
      const updatedAdditional = currentAdditional.filter((item) => item.value !== ioNumberValue);
      helpers.updateField('additionalIONumbers', updatedAdditional);

      if (formValues.IONumber === ioNumberValue) {
        promoteMostRecentIO(updatedAdditional);
      }
      return;
    }

    try {
      await updateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: {
            additionalIONumbers: {
              delete: [{ id: ioNumberId }],
            },
            externalCRMIntegrations: {
              delete: integrationIdsToDelete,
            },
            attachments: {
              delete: attachmentIdsToDelete,
            },
          },
        },
      });

      const currentAdditional = formValues.additionalIONumbers || [];
      const updatedAdditional = currentAdditional.filter((item) => item.value !== ioNumberValue);
      helpers.updateField('additionalIONumbers', updatedAdditional);

      if (formValues.IONumber === ioNumberValue) {
        promoteMostRecentIO(updatedAdditional);
      }

      message.success('IO Number deleted successfully');
    } catch (error) {
      console.error(error);
      message.error('Failed to delete IO Number');
    }
  };

  const handleIONumberChange = (value) => {
    helpers.updateField('IONumber', value);
  };

  const handleAddIOFromSearch = async (value) => {
    if (!value.trim()) return;

    const currentAdditional = formValues.additionalIONumbers || currentCampaignOrder?.additionalIONumbers || [];
    const isDuplicate = currentAdditional.some(
      io => io.value.toLowerCase() === value.toLowerCase()
    );
    if (isDuplicate) {
      message.warning('This IO Number already exists');
      return;
    }

    try {
      if (currentCampaignOrder?.id) {
        const { data } = await updateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              additionalIONumbers: {
                create: { value },
              },
            },
          },
        });

        const updatedCampaignOrder = data?.updateCampaignOrderRequestor;
        if (updatedCampaignOrder?.additionalIONumbers) {
          helpers.updateField('additionalIONumbers', updatedCampaignOrder.additionalIONumbers);
        }
        helpers.updateField('IONumber', value);
      } else {
        const newEntry = {
          value,
          createdAt: new Date().toISOString(),
          __isStaged: true,
        };
        const updatedAdditional = [...currentAdditional, newEntry];

        helpers.updateField('additionalIONumbers', updatedAdditional);
        helpers.updateField('IONumber', value);
      }

      setIOSearchText('');
    } catch (error) {
      console.error('Error adding IO number:', error);
      message.error('Failed to add IO Number');
    }
  };

  // Prepare dropdown options — newest first, active IO marked. Deletion lives
  // in the history popover, not in the dropdown rows.
  const ioNumberSource = formValues.additionalIONumbers || currentCampaignOrder?.additionalIONumbers || [];

  const sortedIONumbers = [...ioNumberSource].sort((a, b) =>
    String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
  );

  const previousIONumbers = sortedIONumbers.filter((io) => io.value !== formValues.IONumber);

  const ioNumberOptions = sortedIONumbers.map((ioNumber) => {
    const isActive = formValues.IONumber === ioNumber.value;
    return {
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{ioNumber.value}</span>
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>
            {isActive && (
              <Tag color="green" style={{ marginRight: 8, lineHeight: '16px' }}>
                Active
              </Tag>
            )}
            {ioNumber.createdAt && `added ${moment(ioNumber.createdAt).format('MMM D, YYYY')}`}
          </span>
        </div>
      ),
      value: ioNumber.value,
    };
  });

  return (
    <>
      <Card id="requester-details" title="CAMPAIGN ORDER">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={20}>
            <Col xs={24} md={10}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Advertiser</Text>
                <Text type="secondary">The one being promoted in this campaign</Text>
                <Form.Item
                  name="advertiserId"
                  rules={[
                    {
                      required: true,
                      message: 'Please select an advertiser',
                    },
                  ]}
                >
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder="Select Advertiser"
                    optionFilterProp="children"
                    loading={loadingChildrenOrgs}
                    onSearch={handleDebouncedAdvertiserSearch}
                    filterOption={(input, option) => {
                      return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    options={advertisers}
                    disabled={!canEdit}
                    value={formValues.advertiserId}
                  />
                </Form.Item>
              </Space>
            </Col>
            <Col xs={24} md={14}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Campaign Name</Text>
                <Text type="secondary">The name you will identify this campaign by</Text>
                <Form.Item
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter campaign name',
                    },
                    {
                      pattern: /^[a-zA-Z0-9\s\-_]*$/,
                      message:
                        'Only letters, numbers, spaces, hyphens, and underscores are allowed',
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter campaign name"
                    value={formValues.name}
                    onChange={handleNameChange}
                  />
                </Form.Item>
              </Space>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col span={24}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Description</Text>
                <Text type="secondary">Optional: Helps to distinguish your campaign</Text>
                <Form.Item name="description">
                  <Input
                    placeholder="Enter optional description for campaign"
                    value={formValues.description}
                  />
                </Form.Item>
              </Space>
            </Col>
          </Row>

          <Row gutter={20}>
            {/* IO Number: visible to processors in their own real session, or to
                self-service users building a campaign whose bill payer holds a
                contract (CORE/ENTERPRISE/CHANNEL_PARTNER) — the advertiser
                itself, or its channel-partner parent (the bill payer for its
                subtree). Uses the effective permission so faithful mode shows
                the org's own flags and work-as-parent inherits the parent's
                self-service. Processors get it only in a real session (never in
                preview), matching the Salesforce-opportunity rule. */}
            {(isRealSessionProcessor(currentUser) ||
              (getEffectivePermission(currentUser).isSelfService &&
                orgTreeHasContract(selectedAdvertiser))) && (
            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>IO Number</Text>
                <Text type="secondary">IO number or account identifier</Text>
                <Form.Item
                  name="IONumber"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter IO Number',
                    },
                  ]}
                >
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder="Select or type new IO Number"
                    value={formValues.IONumber}
                    onChange={handleIONumberChange}
                    options={ioNumberOptions}
                    searchValue={ioSearchText}
                    onSearch={setIOSearchText}
                    onSelect={() => setIOSearchText('')}
                    filterOption={(input, option) =>
                      option.value.toLowerCase().includes(input.toLowerCase())
                    }
                    dropdownRender={(menu) => {
                      const trimmedSearch = ioSearchText.trim();
                      const exactMatchExists = ioNumberSource.some(
                        io => io.value.toLowerCase() === trimmedSearch.toLowerCase()
                      );
                      const showAddOption = trimmedSearch && !exactMatchExists;

                      if (!showAddOption) return menu;

                      return (
                        <>
                          {menu}
                          <Divider style={{ margin: '8px 0' }} />
                          <div
                            style={{ padding: '0 8px 4px', cursor: 'pointer', color: '#1890ff' }}
                            onClick={() => handleAddIOFromSearch(trimmedSearch)}
                          >
                            <PlusOutlined /> Add "{trimmedSearch}" as IO Number
                          </div>
                        </>
                      );
                    }}
                  />
                </Form.Item>
                {previousIONumbers.length > 0 && (
                  <Popover
                    placement="bottomLeft"
                    title="IO Number history"
                    content={
                      <div style={{ maxWidth: 320 }}>
                        {sortedIONumbers.map((io) => {
                          const isActive = formValues.IONumber === io.value;
                          const { integrationIdsToDelete, attachmentIdsToDelete } =
                            collectCascadeDeletions(io.value);
                          const cascadeCount =
                            integrationIdsToDelete.length + attachmentIdsToDelete.length;
                          return (
                            <div
                              key={io.id || io.value}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 0',
                              }}
                            >
                              <span>
                                <span>{io.value}</span>
                                {isActive && (
                                  <Tag color="green" style={{ marginLeft: 8 }}>
                                    Active
                                  </Tag>
                                )}
                                {io.createdAt && (
                                  <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                    added {moment(io.createdAt).format('MMM D, YYYY')}
                                  </Text>
                                )}
                              </span>
                              {!isActive && (
                                <Popconfirm
                                  placement="left"
                                  title={
                                    <div>
                                      <div>Delete {io.value}?</div>
                                      <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 'normal' }}>
                                        {cascadeCount > 0
                                          ? `Also removes ${cascadeCount} linked Salesforce/PandaDoc record${cascadeCount > 1 ? 's' : ''}.`
                                          : 'It will be removed from this campaign’s history.'}
                                      </div>
                                    </div>
                                  }
                                  onConfirm={() => handleDeleteIONumber(io.id, io.value)}
                                  okText="Delete"
                                  cancelText="Cancel"
                                >
                                  <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                                </Popconfirm>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    }
                  >
                    <Text type="secondary" style={{ cursor: 'pointer', fontSize: 12 }}>
                      Previous ({previousIONumbers.length}):{' '}
                      {previousIONumbers.map((io) => io.value).join(' · ')}
                    </Text>
                  </Popover>
                )}
              </Space>
            </Col>
            )}
            {/* Salesforce Opportunity creation: processors in their OWN session
                only — never while previewing (it writes external SF records).
                When ready for self-service, swap this predicate for the rollout flag. */}
            {isRealSessionProcessor(currentUser) && (
            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Salesforce</Text>
                <Text type="secondary">Create a new opportunity in Salesforce</Text>
                <Tooltip
                  title={
                    !formValues.name
                      ? 'Please fill in the Campaign Name before creating a Salesforce Opportunity'
                      : !formValues.budget
                        ? 'Please fill in the Campaign Budget before creating a Salesforce Opportunity'
                        : ''
                  }
                >
                  <span style={{ display: 'inline-block' }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setSfOppName(formValues.name ?? '');
                        setSfBudget(formValues.budget != null ? String(formValues.budget) : '');
                        setIsSfOppModalVisible(true);
                      }}
                      disabled={!selectedAdvertiser || !formValues.budget || !formValues.name}
                    >
                      Create Salesforce Opportunity
                    </Button>
                  </span>
                </Tooltip>
              </Space>
            </Col>
            )}
            <HasAccess roles={['isTrafficker']}>
              <Col span={8}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text>Renewal Status</Text>
                  <Text type="secondary">Renewal status of the campaign</Text>
                  <Form.Item name="renewalStatus">
                    <Select
                      style={{ width: '100%' }}
                      showSearch
                      placeholder="Expected to Renew"
                      value={formValues.renewalStatus}
                      options={[
                        {
                          label: 'Expected to Renew',
                          value: 'EXPECTED_TO_RENEW',
                          key: 1,
                        },
                        {
                          label: 'Not Renewing',
                          value: 'NOT_RENEWING',
                          key: 2,
                        },
                      ]}
                    />
                  </Form.Item>
                </Space>
              </Col>
            </HasAccess>
          </Row>
        </Space>
      </Card>
      <Form.Item name="productCode" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="additionalIONumbers" hidden>
        <Input />
      </Form.Item>

      {isRealSessionProcessor(currentUser) && (
      <Modal
        title="Create Salesforce Opportunity"
        open={isSfOppModalVisible}
        onCancel={() => {
          if (isCreatingSfOpp) return;
          resetSfOppModal();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={resetSfOppModal}
            disabled={isCreatingSfOpp}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleCreateSalesforceOpportunity}
            loading={
              isCreatingSfOpp ||
              loadingCreateSalesforceOpportunity ||
              loadingCreateSalesforceOpportunityLineItems ||
              loadingSalesforceOpportunityProposalNumber
            }
          >
            Confirm
          </Button>,
        ]}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* <div>
            <Text>Opportunity Name</Text>
            <Input
              value={sfOppName}
              onChange={(e) => setSfOppName(e.target.value)}
            />
          </div> */}
          {/* <div>
            <Text>Campaign Budget</Text>
            <Input
              type="number"
              value={sfBudget}
              onChange={(e) => setSfBudget(e.target.value)}
            />
          </div> */}
          <div>
            <Text>Months</Text>
            <Input
              type="number"
              min={1}
              placeholder="Defaults to 3 if left empty"
              value={sfMonths}
              onChange={(e) => setSfMonths(e.target.value)}
            />
          </div>
          {/* <div>
            <Text>Decision Maker First Name</Text>
            <Input
              placeholder="Enter decision maker first name"
              value={sfDecisionMakerFirstName}
              onChange={(e) => setSfDecisionMakerFirstName(e.target.value)}
            />
          </div>
          <div>
            <Text>Decision Maker Last Name</Text>
            <Input
              placeholder="Enter decision maker last name"
              value={sfDecisionMakerLastName}
              onChange={(e) => setSfDecisionMakerLastName(e.target.value)}
            />
          </div>
          <div>
            <Text>{fourthFieldLabel}</Text>
            <Input
              placeholder={fourthFieldPlaceholder}
              value={sfFourthField}
              onChange={(e) => setSfFourthField(e.target.value)}
            />
          </div> */}
        </Space>
      </Modal>
      )}
    </>
  );
};

export default CampaignRequestorDetails;